import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { AuthStorage } from "../../shared/utils/AuthStorage";
import {
    IBCFieldNames,
    cleanGuid
} from "../utils/InternationalBreedingCertificateConstants";
import { getIBCSchema } from "../utils/InternationalBreedingCertificateValidation";
import {
    getIBCDefaultValues,
    mapIBCToFormValues,
    extractStallionDataFromCertificate,
    resetStallionFormFields
} from "../utils/InternationalBreedingCertificateMappers";

/**
 * Hook responsible for IBC Form state, validation schema, and field synchronizations.
 */
export const useIBCForm = ({
    id,
    serviceId,
    defaultServiceDefId,
    currentLanguage,
    termsList = [],
    isReadOnly = false,
    requestData,
    eligibleCertificatesData = [],
    fetchEligibleCertificates,
    translate
}) => {
    const populatedRequestIdRef = useRef(null);

    const [selectedOwnerId, setSelectedOwnerId] = useState(
        AuthStorage.getInitialOwnerId()
    );

    // Declarative schema for form fields and dynamic terms
    const schema = useMemo(
        () => getIBCSchema(translate, termsList, Boolean(id)),
        [translate, termsList, id]
    );

    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues: getIBCDefaultValues(serviceId || defaultServiceDefId)
    });

    const isRequestDataLoaded = Boolean(
        id &&
        requestData &&
        requestData.id &&
        cleanGuid(requestData.id)?.toLowerCase() === cleanGuid(id)?.toLowerCase()
    );

    // Populate form data on Edit or View modes
    useEffect(() => {
        const isAlreadyPopulated =
            cleanGuid(populatedRequestIdRef.current)?.toLowerCase() === cleanGuid(id)?.toLowerCase();

        if (id && isRequestDataLoaded && !isAlreadyPopulated) {
            const resetData = mapIBCToFormValues({
                requestData,
                serviceId,
                currentLanguage,
                termsList,
                isReadOnly,
                id
            });

            methods.reset(resetData);
            populatedRequestIdRef.current = id;

            const dto =
                requestData.internationalBreedingCertificateDto ||
                requestData.coveringCertificates ||
                requestData;

            const ownerToSelect = cleanGuid(dto?.ownerId || requestData.ownerId);
            if (
                ownerToSelect &&
                ownerToSelect.toLowerCase() !== selectedOwnerId?.toLowerCase()
            ) {
                setSelectedOwnerId(ownerToSelect);
            }
        }
    }, [
        id,
        isRequestDataLoaded,
        requestData,
        serviceId,
        currentLanguage,
        termsList,
        isReadOnly,
        methods,
        selectedOwnerId
    ]);

    // Ensure terms are checked on Edit or View modes
    useEffect(() => {
        if (id && termsList.length > 0) {
            termsList.forEach((_, i) => {
                methods.setValue(`term_${i}`, true);
            });
        }
    }, [id, termsList, methods]);

    // Handle Certificate selection change
    const onCertificateChange = useCallback(
        (selected, isUserInitiated = true) => {
            const selectedId = selected?.value;
            const match = eligibleCertificatesData.find(
                (c) => String(c.id) === String(selectedId)
            );
            const stallionFields = extractStallionDataFromCertificate(match, currentLanguage);

            Object.entries(stallionFields).forEach(([field, value]) => {
                methods.setValue(field, value);
            });

            if (isUserInitiated) {
                methods.setValue(IBCFieldNames.LAST_COVERING_DATE, "");
                methods.clearErrors(IBCFieldNames.LAST_COVERING_DATE);
            }
        },
        [eligibleCertificatesData, currentLanguage, methods]
    );

    // Optimized watcher using useWatch instead of methods.watch
    const watchCertId = useWatch({
        control: methods.control,
        name: IBCFieldNames.SEMEN_COLLECTION_CERTIFICATE_ID
    });
    const watchStallionName = useWatch({
        control: methods.control,
        name: IBCFieldNames.STALLION_NAME
    });

    useEffect(() => {
        if (watchCertId && !watchStallionName && eligibleCertificatesData?.length > 0) {
            onCertificateChange({ value: watchCertId }, false);
        }
    }, [watchCertId, watchStallionName, eligibleCertificatesData, onCertificateChange]);

    // Handle owner change: resets stallion fields and refetches certificates
    const handleOwnerChange = useCallback(
        (newOwnerId) => {
            setSelectedOwnerId(newOwnerId);
            resetStallionFormFields(methods);
            if (fetchEligibleCertificates) {
                fetchEligibleCertificates(newOwnerId);
            }
        },
        [fetchEligibleCertificates, methods]
    );

    return {
        methods,
        selectedOwnerId,
        handleOwnerChange,
        onCertificateChange
    };
};
